import React, { useState } from "react";
import { Button, toast } from "@jammable/ui-core";
import { TextArea } from "@jammable/ui-components/Forms";

// Collects a style description (caption) + optional lyrics for Treblo
// generation (utils/treblo.ts). The generated song is then voice-converted to
// the selected model.
export const MAX_CAPTION = 512;
export const MAX_LYRICS = 2000;

export const LYRICS_PLACEHOLDER = `Leave blank to let AI write the lyrics, or:

[Verse 1]
Type your lyrics here...

[Chorus]
Use [Verse], [Chorus], [Bridge] tags to give it structure`;

// Quick-pick presets for the Style field. Clicking one replaces the caption
// so users can tweak from a sensible starting point instead of staring at a
// blank textarea.
export const GENRE_PRESETS = [
  {
    label: "Pop",
    caption:
      "soft modern pop, catchy melody, warm female vocal, polished production",
  },
  {
    label: "Trap",
    caption: "melodic trap, crisp hi-hats, deep 808 bass, confident male vocal",
  },
  {
    label: "R&B",
    caption:
      "smooth modern R&B, sultry female vocal, warm Rhodes piano, late night",
  },
  {
    label: "Indie",
    caption:
      "indie folk, fingerpicked acoustic guitar, warm reflective male vocal",
  },
  {
    label: "K-pop",
    caption:
      "upbeat K-pop dance, energetic female vocal, catchy synth hook, bright",
  },
  {
    label: "EDM",
    caption:
      "uplifting electronic dance, four-on-the-floor beat, anthemic vocal, festival energy",
  },
  {
    label: "Lo-fi",
    caption:
      "lo-fi chill, soft jazz piano, dreamy female vocal, rainy day mood",
  },
  {
    label: "Country",
    caption: "modern country, warm male vocal, acoustic guitar, heartland feel",
  },
];

const GenerateSongView = ({ onGenerateConfirmed, voiceModelName }) => {
  const [caption, setCaption] = useState("");
  const [lyrics, setLyrics] = useState("");

  const handleConfirm = () => {
    if (!caption.trim()) {
      toast.error("Describe the style of the song.");
      return;
    }
    if (caption.length > MAX_CAPTION) {
      toast.error(`Style description must be under ${MAX_CAPTION} characters.`);
      return;
    }
    if (lyrics.length > MAX_LYRICS) {
      toast.error(`Lyrics must be under ${MAX_LYRICS} characters.`);
      return;
    }
    onGenerateConfirmed({
      caption: caption.trim(),
      lyrics: lyrics.trim(),
      voice_model_name: voiceModelName || "",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <span className="tw-font-bold tw-text-sm tw-opacity-70">Style</span>
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          {GENRE_PRESETS.map((g) => (
            <button
              key={g.label}
              type="button"
              onClick={() => setCaption(g.caption)}
              className="tw-text-xs tw-font-bold tw-bg-gray-100 hover:tw-bg-purple-50 hover:tw-text-purple-800 hover:tw-border-purple-200 tw-text-gray-700 tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-transition"
            >
              {g.label}
            </button>
          ))}
        </div>
        <TextArea
          placeholder="e.g. melodic trap with crisp hi-hats, deep 808 bass, confident male vocal, polished modern production"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          minRows={3}
          className="tw-w-full tw-bg-gray-100"
          maxLength={MAX_CAPTION + 50}
        />
        <span
          className={`tw-self-end tw-text-xs tw-font-bold ${
            caption.length > MAX_CAPTION ? "tw-text-red-600" : "tw-opacity-40"
          }`}
        >
          {caption.length}/{MAX_CAPTION}
        </span>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-font-bold tw-text-sm tw-opacity-70">
          Lyrics{" "}
          <span className="tw-opacity-50 tw-font-normal">(optional)</span>
        </span>
        <TextArea
          placeholder={LYRICS_PLACEHOLDER}
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          minRows={6}
          className="tw-w-full tw-bg-gray-100"
          maxLength={MAX_LYRICS + 50}
        />
        <span
          className={`tw-self-end tw-text-xs tw-font-bold ${
            lyrics.length > MAX_LYRICS ? "tw-text-red-600" : "tw-opacity-40"
          }`}
        >
          {lyrics.length}/{MAX_LYRICS}
        </span>
      </div>

      {voiceModelName ? (
        <div className="tw-bg-purple-50 tw-text-purple-800 tw-rounded-xl tw-p-3 tw-text-sm tw-font-semibold">
          We&apos;ll generate the song, then sing it in{" "}
          <span className="tw-font-black">{voiceModelName}</span>&apos;s voice.
        </div>
      ) : null}

      <Button
        onClick={handleConfirm}
        disabled={!caption.trim()}
        className="tw-w-full tw-font-black tw-uppercase tw-text-base tw-rounded-2xl tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        Use This Song
      </Button>

      {/* Required by Treblo's API terms: a prominent, legible link to
          treblo.com wherever generation is offered. Do not remove or shrink
          it without a waiver from api@treblo.com. */}
      <a
        href="https://treblo.com"
        target="_blank"
        rel="noopener noreferrer"
        className="tw-self-center tw-text-sm tw-font-bold tw-text-gray-600 hover:tw-text-purple-700 tw-underline tw-underline-offset-2"
      >
        Powered by Treblo
      </a>
    </div>
  );
};

export default GenerateSongView;
